import { useEffect, useRef } from "react";

/**
 * While `active` is truthy (an HTML5 drag is in progress), listens for
 * `dragover` events and scrolls the nearest scrollable ancestor of the
 * element currently under the cursor when the cursor is near the top or
 * bottom edge of that container. The native HTML5 drag only auto-scrolls
 * the window itself, so for scrollable panels inside the page the user
 * can't reach rows that are off-screen without this helper.
 */
export function useEdgeAutoScroll(active) {
  const yRef = useRef(0);
  const xRef = useRef(0);
  const scrollElRef = useRef(null);
  const rafRef = useRef(null);
  const EDGE = 80;
  const MAX_SPEED = 16;

  useEffect(() => {
    if (!active) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      scrollElRef.current = null;
      return undefined;
    }

    const findScroll = (el) => {
      let node = el && el.parentElement;
      while (node) {
        const s = window.getComputedStyle(node);
        if (/(auto|scroll|overlay)/.test(s.overflowY) && node.scrollHeight > node.clientHeight + 1) {
          return node;
        }
        node = node.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };

    const tick = () => {
      const el = scrollElRef.current;
      if (!el) { rafRef.current = requestAnimationFrame(tick); return; }
      const rect = el.getBoundingClientRect();
      const y = yRef.current;
      let delta = 0;
      if (y < rect.top + EDGE) {
        delta = -Math.min(MAX_SPEED, (rect.top + EDGE - y) / 3);
      } else if (y > rect.bottom - EDGE) {
        delta = Math.min(MAX_SPEED, (y - (rect.bottom - EDGE)) / 3);
      }
      if (delta !== 0) el.scrollTop += delta;
      rafRef.current = requestAnimationFrame(tick);
    };

    const onPoint = (e) => {
      // dragover fires with clientX/Y like mouse events.
      if (typeof e.clientY === "number") yRef.current = e.clientY;
      if (typeof e.clientX === "number") xRef.current = e.clientX;
      // Resolve the scroll parent lazily from whatever row is under the
      // cursor right now — the dragged item may have left the sidebar for
      // the chat scroller.
      const under = document.elementFromPoint(xRef.current, yRef.current);
      const row = under && under.closest ? under.closest("[data-reorder-id]") : null;
      if (row) {
        const sp = findScroll(row);
        if (sp !== scrollElRef.current) scrollElRef.current = sp;
      }
    };

    window.addEventListener("dragover", onPoint);
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("dragover", onPoint);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      scrollElRef.current = null;
    };
  }, [active]);
}
